const HIDE_DELAY = 2500;

function fmt(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

export class VideoOverlay {
  private root: HTMLElement;
  private videoEl: HTMLVideoElement | null = null;
  private controls: HTMLElement;
  private seekBar: HTMLInputElement;
  private timeEl: HTMLElement;
  private playBtn: HTMLElement;
  private volBtn: HTMLElement;
  private closeBtn: HTMLElement;
  private isOpen = false;
  private hideTimer: ReturnType<typeof setTimeout> | null = null;
  private rafId: number | null = null;
  private isSeeking = false;

  // Bound handlers
  private _onKey: (e: KeyboardEvent) => void;
  private _onMove: () => void;
  private _onClose: () => void;

  constructor() {
    // Root overlay
    this.root = document.createElement("div");
    this.root.className = "vo";

    // Video container
    const wrap = document.createElement("div");
    wrap.className = "vo_wrap";
    this.root.appendChild(wrap);

    // Close button
    this.closeBtn = document.createElement("button");
    this.closeBtn.className = "vo_close";
    this.closeBtn.setAttribute("aria-label", "Close");
    this.closeBtn.innerHTML = `<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 1L13 13M13 1L1 13" stroke="currentColor" stroke-width="1"/></svg><span>Close</span>`;
    this.root.appendChild(this.closeBtn);

    // Controls bar
    this.controls = document.createElement("div");
    this.controls.className = "vo_ctrl";

    // Play/Pause
    this.playBtn = document.createElement("button");
    this.playBtn.className = "vo_ctrl_play";
    this.playBtn.setAttribute("aria-label", "Play/Pause");
    this.playBtn.innerHTML = this._pauseIcon();

    // Time
    this.timeEl = document.createElement("span");
    this.timeEl.className = "vo_ctrl_time";
    this.timeEl.textContent = "0:00:00";

    // Seek
    this.seekBar = document.createElement("input");
    this.seekBar.className = "vo_ctrl_seek";
    this.seekBar.type = "range";
    this.seekBar.min = "0";
    this.seekBar.max = "1000";
    this.seekBar.value = "0";
    this.seekBar.step = "1";

    // Volume
    this.volBtn = document.createElement("button");
    this.volBtn.className = "vo_ctrl_vol";
    this.volBtn.setAttribute("aria-label", "Toggle Mute");
    this.volBtn.innerHTML = this._muteIcon();

    this.controls.appendChild(this.playBtn);
    this.controls.appendChild(this.timeEl);
    this.controls.appendChild(this.seekBar);
    this.controls.appendChild(this.volBtn);
    this.root.appendChild(this.controls);

    document.body.appendChild(this.root);

    // Bind handlers
    this._onKey = (e: KeyboardEvent) => this._handleKey(e);
    this._onMove = () => this._showControls();
    this._onClose = () => this.close();

    // Wire controls
    this.closeBtn.addEventListener("click", this._onClose);
    this.playBtn.addEventListener("click", () => this._togglePlay());
    this.volBtn.addEventListener("click", () => this._toggleMute());

    this.seekBar.addEventListener("input", () => {
      this.isSeeking = true;
      if (this.videoEl) {
        const t =
          (parseFloat(this.seekBar.value) / 1000) * this.videoEl.duration;
        if (isFinite(t)) this.videoEl.currentTime = t;
      }
    });
    this.seekBar.addEventListener("change", () => {
      this.isSeeking = false;
    });
  }

  init(): void {
    document.addEventListener("click", (e: MouseEvent) => {
      const target = e.target as HTMLElement;

      // Play button (project page or home)
      const playBtn = target.closest(".pj-play") as HTMLElement | null;
      if (playBtn) {
        const item = playBtn.closest(".pj-item") || playBtn.closest(".l-bg");
        const video = item?.querySelector("video") as HTMLVideoElement | null;
        if (video) {
          e.preventDefault();
          e.stopPropagation();
          this.open(video);
        }
        return;
      }

      // Play detail page: open via the PLAY VIDEO sticker
      const plyBtn = target.closest(".ply-play") as HTMLElement | null;
      if (plyBtn) {
        const item = plyBtn.closest(".ply-item");
        const video = item?.querySelector("video") as HTMLVideoElement | null;
        if (video) {
          e.preventDefault();
          e.stopPropagation();
          this.open(video);
        }
        return;
      }

      // Click on .g__l with video opens overlay (project pages + all others)
      const slot = target.closest(".g__l");
      if (!slot) return;
      if (slot.closest(".p")) return;
      if (slot.closest(".pl")) return;
      const video = slot.querySelector("video") as HTMLVideoElement | null;
      if (!video) return;
      e.preventDefault();
      e.stopPropagation();
      this.open(video);
    });
  }

  open(source: HTMLVideoElement): void {
    if (this.isOpen) return;
    this.isOpen = true;

    // Create overlay video from same source
    const v = document.createElement("video");
    v.src = source.src;
    v.crossOrigin = "anonymous";
    v.playsInline = true;
    v.autoplay = true;
    v.muted = false;
    v.loop = true;
    v.className = "vo_video";

    // Always start from beginning
    v.currentTime = 0;

    const wrap = this.root.querySelector(".vo_wrap")!;
    wrap.innerHTML = "";
    wrap.appendChild(v);
    this.videoEl = v;

    // UI state
    this.volBtn.innerHTML = v.muted ? this._muteIcon() : this._unmuteIcon();
    this.playBtn.innerHTML = this._pauseIcon();
    this.seekBar.value = "0";
    this.timeEl.textContent = "0:00:00";

    // Show overlay
    this.root.classList.add("is-open");

    // Hide nav
    const nav = document.getElementById("nav");
    nav?.classList.add("is-hidden");

    // Listeners
    window.addEventListener("keydown", this._onKey);
    this.root.addEventListener("mousemove", this._onMove);
    this.root.addEventListener("touchstart", this._onMove, { passive: true });

    // Show controls initially, then auto-hide
    this._showControls();

    // Start render loop for seek/time
    this._startLoop();

    v.play().catch(() => {
      // autoplay may be blocked, update icon
      this.playBtn.innerHTML = this._playIcon();
    });
  }

  close(): void {
    if (!this.isOpen) return;
    this.isOpen = false;

    // Stop overlay video
    if (this.videoEl) {
      this.videoEl.pause();
      this.videoEl.removeAttribute("src");
      this.videoEl.load();
      this.videoEl = null;
    }

    // Clear wrap
    const wrap = this.root.querySelector(".vo_wrap");
    if (wrap) wrap.innerHTML = "";

    // Hide overlay
    this.root.classList.remove("is-open");

    // Show nav (unless on home page)
    const nav = document.getElementById("nav");
    const path = location.pathname;
    if (path !== "/" && path !== "/index") {
      nav?.classList.remove("is-hidden");
    }

    // Remove listeners
    window.removeEventListener("keydown", this._onKey);
    this.root.removeEventListener("mousemove", this._onMove);
    this.root.removeEventListener("touchstart", this._onMove);

    this._stopLoop();
    if (this.hideTimer) clearTimeout(this.hideTimer);
  }

  private _handleKey(e: KeyboardEvent): void {
    if (e.key === "Escape") {
      this.close();
      return;
    }
    if (e.key === " " || e.code === "Space") {
      e.preventDefault();
      this._togglePlay();
    }
  }

  private _togglePlay(): void {
    if (!this.videoEl) return;
    if (this.videoEl.paused) {
      this.videoEl.play();
      this.playBtn.innerHTML = this._pauseIcon();
    } else {
      this.videoEl.pause();
      this.playBtn.innerHTML = this._playIcon();
    }
  }

  private _toggleMute(): void {
    if (!this.videoEl) return;
    this.videoEl.muted = !this.videoEl.muted;
    this.volBtn.innerHTML = this.videoEl.muted
      ? this._muteIcon()
      : this._unmuteIcon();
  }

  private _showControls(): void {
    this.root.classList.add("vo--active");
    if (this.hideTimer) clearTimeout(this.hideTimer);
    this.hideTimer = setTimeout(() => {
      this.root.classList.remove("vo--active");
    }, HIDE_DELAY);
  }

  private _startLoop(): void {
    const tick = () => {
      if (!this.isOpen) return;
      this._updateSeek();
      this.rafId = requestAnimationFrame(tick);
    };
    this.rafId = requestAnimationFrame(tick);
  }

  private _stopLoop(): void {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  private _updateSeek(): void {
    const v = this.videoEl;
    if (!v || !isFinite(v.duration)) return;
    const ratio = v.currentTime / v.duration;
    if (!this.isSeeking) {
      const pct = ratio * 1000;
      this.seekBar.value = String(pct);
    }
    this.seekBar.style.setProperty("--seek-pct", String(ratio));
    this.timeEl.textContent = fmt(v.currentTime);
  }

  private _playIcon(): string {
    return `<svg width="16" height="18" viewBox="0 0 16 18" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 1L15 9L1 17V1Z" stroke="currentColor" stroke-width="1" fill="currentColor"/></svg>`;
  }

  private _pauseIcon(): string {
    return `<svg width="14" height="18" viewBox="0 0 14 18" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="1" y="1" width="3" height="16" stroke="currentColor" stroke-width="1" fill="currentColor"/><rect x="10" y="1" width="3" height="16" stroke="currentColor" stroke-width="1" fill="currentColor"/></svg>`;
  }

  private _muteIcon(): string {
    return `<svg width="20" height="18" viewBox="0 0 20 18" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 6.5V11.5H4.5L9 16V2L4.5 6.5H1Z" stroke="currentColor" stroke-width="1"/><path d="M14 4L18 14M18 4L14 14" stroke="currentColor" stroke-width="1"/></svg>`;
  }

  private _unmuteIcon(): string {
    return `<svg width="20" height="18" viewBox="0 0 20 18" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 6.5V11.5H4.5L9 16V2L4.5 6.5H1Z" stroke="currentColor" stroke-width="1"/><path d="M13 5.5C14.3 6.8 15 8.3 15 9.5S14.3 12.2 13 13.5" stroke="currentColor" stroke-width="1" fill="none"/><path d="M15.5 3C17.5 5 18.5 7.2 18.5 9.5S17.5 14 15.5 16" stroke="currentColor" stroke-width="1" fill="none"/></svg>`;
  }
}
